import { api } from "./api";
import { playTone, vibeKey } from "./sound";

const b64ToBytes = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw], (c) => c.charCodeAt(0));
};

export const pushSupported = () => typeof window !== "undefined" && !window.Capacitor?.isNativePlatform?.() && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export const pushPermission = () => (pushSupported() ? Notification.permission : "unsupported");

async function subscribeAndSave() {
  const reg = await navigator.serviceWorker.register("/connect-sw.js", { scope: "/connect" });
  await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  if (!data.publicKey) throw new Error("Push is not configured on the server");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.publicKey) });
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

/** Called from a user gesture: asks permission then registers the device. */
export async function enablePush() {
  if (!pushSupported()) throw new Error("This browser does not support push notifications");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error(perm === "denied" ? "Notifications are blocked in browser settings" : "Permission not granted");
  return subscribeAndSave();
}

/** Capacitor APK: register with Firebase Cloud Messaging through the native plugin (active once the FCM build ships). */
const ackPush = (kind) => api.post("/push/ack", { token: localStorage.getItem("nxc_fcm_token") || null, kind }).catch(() => {});
export const nativeAlerts = () => (typeof window !== "undefined" && window.NxcAlerts) || null;
/** APK ≥1.4: channels are created natively (per-pattern vibration, alarm-class CEO channel). Older APKs fall back to the Capacitor plugin channels. */
export const setupNativeChannels = async (PN) => {
  const NA = nativeAlerts();
  if (NA) { try { NA.setupChannels(vibeKey()); return "v3"; } catch (_) {} }
  if (!PN) return null;
  await PN.createChannel({ id: "nexsecure_msg_v2", name: "Messages", description: "New messages and announcements", importance: 5, visibility: 1, vibration: true, sound: "nexsecure_message.wav" }).catch(() => {});
  await PN.createChannel({ id: "nexsecure_ceo_v2", name: "CEO alerts", description: "Puppet inbox and system alerts", importance: 5, visibility: 1, vibration: true, sound: "nexsecure_ceo.wav" }).catch(() => {});
  return "v2";
};
async function syncNativePush() {
  const PN = window.Capacitor?.Plugins?.PushNotifications;
  if (!PN) return false;
  const perm = await PN.requestPermissions();
  if (perm.receive !== "granted") return true;
  const channels = await setupNativeChannels(PN);
  PN.deleteChannel({ id: "nexsecure_connect" }).catch(() => {});
  const model = (navigator.userAgent.match(/;\s*([^;)]+?)\s+Build\//) || [])[1] || "Android";
  PN.addListener("registration", (t) => { localStorage.setItem("nxc_fcm_token", t.value); api.post("/push/fcm-token", { token: t.value, platform: "android", device: model, app_version: (navigator.userAgent.match(/NexSecureConnect\/([\d.]+)/) || [])[1] || null, channels }).catch(() => {}); });
  PN.addListener("pushNotificationReceived", (n) => { ackPush("received"); playTone(n?.data?.sound || "message", true); });
  PN.addListener("pushNotificationActionPerformed", (a) => { ackPush("opened"); const url = a?.notification?.data?.url; if (url) window.location.assign(url); });
  await PN.register();
  return true;
}

/** Silent re-sync on login when permission was already granted (keeps the subscription tied to this account). */
export async function syncPush() {
  if (await syncNativePush().catch(() => false)) return;
  if (!pushSupported() || Notification.permission !== "granted") return;
  try { await subscribeAndSave(); } catch (_) {}
}

export async function disablePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration("/connect");
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api.delete("/push/subscribe", { params: { endpoint: sub.endpoint } }).catch(() => {});
  await sub.unsubscribe();
}
